import { Col, Form, Input, InputNumber, Row, Select } from "antd";
import { memo, useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useHttp } from "../../hooks/useHttp";
import { SelectStage } from "~components/SelectStage/SelectStage";
import { SelectPool } from "~components/SelectPool/SelectPool";
const { TextArea } = Input;

const AddStandingsForm = (props) => {
  const { handleSetFormRef, entityList, conferenceList, defaultCalculatedSchema, defaultPointsSchema } = props;
  const { sport, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const [form] = Form.useForm();
  const [selectedConferenceId, setSelectedConferenceId] = useState(null);
  const [divisionsData, setDivisionsData] = useState([]);
  const { loading, request, error, cleanError } = useHttp();

  const initValues = {
    fixtureType: "REGULAR",
    position: 1,
    groupingBase: "OVERALL",
    groupingConferenceDivision: "OVERALL",
    groupingStagePool: "OVERALL",
    live: false,
    calculated: JSON.stringify(defaultCalculatedSchema),
    points: JSON.stringify(defaultPointsSchema),
  };

  const fetchDivisions = useCallback(async () => {
    try {
      const res = await request({
        url: `/v1/${sport}/o/${organizationId}/conferences/${selectedConferenceId}/divisions`,
      });
      setDivisionsData(res?.data || []);
    } catch (e) {
      console.error(e);
    }
  }, [sport, organizationId, selectedConferenceId, request]);

  useEffect(() => {
    handleSetFormRef(form);
  }, []);

  useEffect(() => {
    if (selectedConferenceId) {
      fetchDivisions();
    }
  }, [selectedConferenceId]);

  const FIXTURE_TYPES = ["ALL_STAR", "PRESEASON", "REGULAR", "FINAL", "PLAYOFF", "FRIENDLY", "DEMONSTRATION"];
  const GROUPING_BASES = ["ROUND", "OVERALL"];
  const GROUPING_CONFERENCE_DIVISIONS = ["CONFERENCE", "DIVISION", "OVERALL"];
  const GROUPING_STAGE_POOLS = ["STAGE", "STAGEPOOL", "OVERALL"];

  const handleConferenceChange = (value) => {
    setSelectedConferenceId(value);
  };

  const renderDivision = ({ divisionId, nameLocal }) => (
    <Select.Option value={divisionId} key={divisionId}>
      {nameLocal}
    </Select.Option>
  );

  return (
    <Form
      form={form}
      labelCol={{
        span: 10,
      }}
      colon={false}
      labelWrap={true}
      initialValues={initValues}
    >
      <Row>
        <Col span={24}>
          <Form.Item
            labelCol={{
              span: 4,
            }}
            name={"entityId"}
            label={formatMessage({
              id: "team",
              defaultMessage: "Team",
            })}
            tooltip={formatMessage({
              id: "form.help.standings.row.team",
              defaultMessage: "The team to add standings data for",
            })}
            rules={[{ required: true }]}
          >
            <Select>
              {entityList?.map((entity) => (
                <Select.Option value={entity.entityId} key={entity.entityId}>
                  {entity.nameFullLocal}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={11}>
          <Form.Item
            labelCol={{
              span: 10,
            }}
            name={"conferenceId"}
            label={formatMessage({
              id: "conference",
              defaultMessage: "Conference",
            })}
            tooltip={formatMessage({
              id: "form.help.standings.row.conference",
              defaultMessage: "The related conference for the standings",
            })}
          >
            <Select onChange={handleConferenceChange}>
              <Select.Option value={""}></Select.Option>
              {conferenceList?.map((conference) => (
                <Select.Option value={conference.conferenceId} key={conference.conferenceId}>
                  {conference.nameLocal}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12} offset={1}>
          <Form.Item
            labelCol={{
              span: 7,
            }}
            name={"divisionId"}
            label={formatMessage({
              id: "division",
              defaultMessage: "Division",
            })}
            tooltip={formatMessage({
              id: "form.help.standings.row.division",
              defaultMessage: "The related conference for the standings",
            })}
            dependencies={["conferenceId"]}
          >
            <Select disabled={!selectedConferenceId || !divisionsData.length}>
              <Select.Option value={""}></Select.Option>
              {divisionsData.map(renderDivision)}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            labelCol={{
              span: 9,
            }}
            name={"stageCode"}
            label={formatMessage({
              id: "stage",
            })}
            tooltip={formatMessage({
              id: "form.help.standings.row.stage",
            })}
          >
            <SelectStage />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            labelCol={{
              span: 9,
            }}
            name={"poolCode"}
            label={formatMessage({
              id: "pool",
            })}
            tooltip={formatMessage({
              id: "form.help.standings.row.pool",
            })}
          >
            <SelectPool />
          </Form.Item>
        </Col>
        <Col span={14}>
          <Form.Item
            labelCol={{
              span: 9,
            }}
            name={"fixtureType"}
            label={formatMessage({
              id: "fixture.type",
              defaultMessage: "Fixture Type",
            })}
            tooltip={formatMessage({
              id: "form.help.standings.row.fixture.type",
              defaultMessage: "The related fixture type for the standings",
            })}
            rules={[{ required: true }]}
          >
            <Select>
              {FIXTURE_TYPES?.map((fixtureType) => (
                <Select.Option value={fixtureType} key={fixtureType}>
                  {fixtureType}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={10}>
          <Form.Item
            labelCol={{
              span: 12,
            }}
            name={"position"}
            label={formatMessage({
              id: "position",
              defaultMessage: "Position",
            })}
            tooltip={formatMessage({
              id: "form.help.standings.row.position",
              defaultMessage: "The position for the selected team",
            })}
            required={true}
          >
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            labelCol={{
              span: 12,
            }}
            name={"groupingBase"}
            label={formatMessage({
              id: "groupingBase",
              defaultMessage: "Grouping Base",
            })}
            tooltip={formatMessage({
              id: "form.help.standings.row.grouping.base",
              defaultMessage: "The grouping base for the standings",
            })}
            rules={[{ required: true }]}
          >
            <Select>
              {GROUPING_BASES?.map((groupingBase) => (
                <Select.Option value={groupingBase} key={groupingBase}>
                  {groupingBase}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={16}>
          <Form.Item
            labelCol={{
              span: 16,
            }}
            name={"groupingConferenceDivision"}
            label={formatMessage({
              id: "groupingConferenceDivision",
              defaultMessage: "Grouping Conference/Division",
            })}
            tooltip={formatMessage({
              id: "form.help.standings.row.grouping.conference.division",
              defaultMessage: "The grouping conference/division for the standings",
            })}
            rules={[{ required: true }]}
          >
            <Select>
              {GROUPING_CONFERENCE_DIVISIONS?.map((groupingConferenceDivision) => (
                <Select.Option value={groupingConferenceDivision} key={groupingConferenceDivision}>
                  {groupingConferenceDivision}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={16}>
          <Form.Item
            labelCol={{
              span: 12,
            }}
            name={"groupingStagePool"}
            label={formatMessage({
              id: "groupingStagePool",
              defaultMessage: "Grouping Stage/Pool",
            })}
            tooltip={formatMessage({
              id: "form.help.standings.row.grouping.stage.pool",
              defaultMessage: "The grouping stage/pool for the standings",
            })}
            rules={[{ required: true }]}
          >
            <Select>
              {GROUPING_STAGE_POOLS?.map((groupingStagePool) => (
                <Select.Option value={groupingStagePool} key={groupingStagePool}>
                  {groupingStagePool}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={13}>
          <Form.Item
            labelCol={{
              span: 14,
            }}
            name={"live"}
            label={formatMessage({
              id: "include.live.fixtures",
              defaultMessage: "Include Live fixtures",
            })}
            tooltip={formatMessage({
              id: "form.help.standings.row.live",
              defaultMessage: "Include live fixtures for the standings",
            })}
          >
            <Select>
              <Select.Option value={true}>Yes</Select.Option>
              <Select.Option value={false}>No</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            labelCol={{
              span: 4,
            }}
            name={"calculated"}
            label={formatMessage({
              id: "calculated",
              defaultMessage: "Calculated",
            })}
            tooltip={formatMessage({
              id: "form.help.standings.row.calculated",
              defaultMessage: "The team data calculated points values for the standings",
            })}
          >
            <TextArea rows={4} />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            labelCol={{
              span: 4,
            }}
            name={"points"}
            label={formatMessage({
              id: "points",
              defaultMessage: "Points",
            })}
            tooltip={formatMessage({
              id: "form.help.standings.row.points",
              defaultMessage: "The team data points values for the standings",
            })}
          >
            <TextArea rows={4} />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default memo(AddStandingsForm);
